import * as i0 from '@angular/core';
import { ElementRef, Component, ViewEncapsulation, Input, ViewChild, NgModule, CUSTOM_ELEMENTS_SCHEMA, Injectable, EventEmitter, ChangeDetectionStrategy, Output } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import * as i2 from '@angular/material/tree';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import * as i1 from '@sick-curie/dashboard-builder-core';
import { BindingHelper, retryWithDelay, FunctionExecutionService, SpecializedBindingPropertiesComponent, DashboardBuilderCoreModule, BestEffortSingleQueryTimer, FunctionExecutionResultComponent } from '@sick-curie/dashboard-builder-core';
import { __decorate, __metadata } from 'tslib';
import { Describe, AngularDashboardElement, AngularPropertiesElement } from '@sick-curie/dashboard-builder-ui-models';
import { Subject, firstValueFrom, of, BehaviorSubject, fromEvent, ReplaySubject, defer, EMPTY, throwError, tap } from 'rxjs';
import { scan, takeUntil, debounceTime, map, startWith, groupBy, first, share, multicast, find, mergeMap, switchMap, catchError, withLatestFrom, finalize, tap as tap$1 } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { NestedTreeControl } from '@angular/cdk/tree';
import { webSocket } from 'rxjs/webSocket';

var FunctionBindingComponent_1;
let FunctionBindingComponent = FunctionBindingComponent_1 = class FunctionBindingComponent {
    constructor(elRef, functionExecutionService) {
        this.functionExecutionService = functionExecutionService;
        this.updatePeriodMs = 1000;
        this.clearSession$ = new Subject();
        this.helper = new BindingHelper(elRef, functionExecutionService);
    }
    async ngOnChanges() {
        this.clearSession$.next();
        if (!this.fun) {
            return;
        }
        const funIsValid = await firstValueFrom(this.functionExecutionService.isValid(this.fun));
        this.helper.dispatchError(FunctionBindingComponent_1.ERROR_FUNCTION_SYNTAX, undefined, funIsValid);
        if (!funIsValid) {
            return;
        }
        of(0)
            .pipe(this.helper.poll(this.updatePeriodMs), scan((acc) => acc + 1, -1), this.helper.applyPostProcessor(this.fun), takeUntil(this.clearSession$), retryWithDelay(this.updatePeriodMs))
            .subscribe((data) => this.helper.setValue(this.property, data));
    }
    ngOnDestroy() {
        this.clearSession$.next();
    }
};
FunctionBindingComponent.ERROR_FUNCTION_SYNTAX = 'The function you entered is not valid.\n\nPlease make sure there are no typos or JavaScript syntax errors.';
FunctionBindingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionBindingComponent, deps: [{ token: i0.ElementRef }, { token: i1.FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
FunctionBindingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: FunctionBindingComponent, selector: "dashboard-builder-function-binding", inputs: { property: "property", fun: "fun", updatePeriodMs: "updatePeriodMs" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true, encapsulation: i0.ViewEncapsulation.ShadowDom });
__decorate([
    Describe('JavaScript function to calculate the value e.g. (x) => x + 1'),
    __metadata("design:type", String)
], FunctionBindingComponent.prototype, "fun", void 0);
__decorate([
    Describe('Update interval in milliseconds'),
    __metadata("design:type", Object)
], FunctionBindingComponent.prototype, "updatePeriodMs", void 0);
FunctionBindingComponent = FunctionBindingComponent_1 = __decorate([
    AngularDashboardElement({
        tag: 'dashboard-function-binding',
        role: 'behavior',
        documentation: {
            name: 'Function Binding',
            description: '',
        },
    }),
    __metadata("design:paramtypes", [ElementRef, FunctionExecutionService])
], FunctionBindingComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionBindingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dashboard-builder-function-binding',
                    template: '',
                    encapsulation: ViewEncapsulation.ShadowDom,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.FunctionExecutionService }]; }, propDecorators: { property: [{
                type: Input
            }], fun: [{
                type: Input
            }], updatePeriodMs: [{
                type: Input
            }] } });

var FunctionBindingPropertiesComponent_1;
let FunctionBindingPropertiesComponent = FunctionBindingPropertiesComponent_1 = class FunctionBindingPropertiesComponent extends SpecializedBindingPropertiesComponent {
    constructor(functionExecutionService) {
        super();
        this.functionExecutionService = functionExecutionService;
        this.bindingValue$ = new BehaviorSubject(undefined);
        this.isCodeValid = false;
    }
    bindingValueChanged(value) {
        this.bindingValue$.next(value.detail.value);
    }
    ngOnInit() {
        fromEvent(this.codeArea.nativeElement, 'input')
            .pipe(debounceTime(400), map((ev) => ev.detail), startWith(this.binding.properties.fun))
            .subscribe({
            next: async (code) => {
                if (!code) {
                    this.message = FunctionBindingPropertiesComponent_1.FUNCTION_HELP;
                    this.isCodeValid = false;
                    this.bindingValue$.next(undefined);
                    return;
                }
                this.isCodeValid = await firstValueFrom(this.functionExecutionService.isValid(code));
                if (!this.isCodeValid) {
                    this.message = FunctionBindingPropertiesComponent_1.FUNCTION_INVALID;
                    this.bindingValue$.next(undefined);
                }
                else {
                    this.message = undefined;
                }
            },
        });
    }
    formHasErrors() {
        return !this.isCodeValid;
    }
};
FunctionBindingPropertiesComponent.FUNCTION_HELP = {
    text: 'Enter JavaScript function to calculate the value e.g (x) => x + 1',
    type: 'info',
};
FunctionBindingPropertiesComponent.FUNCTION_INVALID = {
    text: FunctionBindingComponent.ERROR_FUNCTION_SYNTAX,
    type: 'error',
};
FunctionBindingPropertiesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionBindingPropertiesComponent, deps: [{ token: i1.FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
FunctionBindingPropertiesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: FunctionBindingPropertiesComponent, selector: "dashboard-function-binding-properties", viewQueries: [{ propertyName: "codeArea", first: true, predicate: ["codeArea"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"main\">\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"fun\">Function:</div>\n    <dashboard-builder-user-input\n      [error]=\"message?.type === 'error' ? message.text : ''\"\n      [info]=\"message?.type === 'info' ? message.text : ''\"\n    >\n      <davinci-text-area #codeArea multiline [linkToElement]=\"binding\" linkToProperty=\"fun\"></davinci-text-area>\n    </dashboard-builder-user-input>\n  </div>\n  <div class=\"grid-columns-4\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"updatePeriodMs\">Interval:</div>\n    <davinci-numeric-field\n      unit=\"ms\"\n      [linkToElement]=\"binding\"\n      linkToProperty=\"updatePeriodMs\"\n      min=\"0\"\n    ></davinci-numeric-field>\n  </div>\n\n  <dashboard-builder-post-processor-result *ngIf=\"message?.type !== 'error'\" [value]=\"bindingValue$ | async\">\n    <dashboard-function-binding\n      (bindingValueChanged)=\"bindingValueChanged($event)\"\n      *ngIf=\"binding.properties.fun\"\n      [fun]=\"binding.properties.fun\"\n      property=\"value\"\n    ></dashboard-function-binding>\n  </dashboard-builder-post-processor-result>\n</div>\n", styles: ["davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}.main{margin-top:-16px;padding-top:16px;padding-bottom:16px;border-top:1px solid var(--davinci-color-outline-light,#C5CACC)}.grid-columns-2,.grid-columns-4{position:relative;display:grid;grid-gap:10px 40px;gap:10px 40px;align-items:center;margin-bottom:1.6em;padding-right:36px}.grid-columns-2{grid-template-columns:120px 1fr}.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}\n"], components: [{ type: i1.HelpStateComponent, selector: "[helptext]", inputs: ["element", "property", "helptext", "hPos"] }, { type: i1.UserInputComponent, selector: "dashboard-builder-user-input", inputs: ["error", "warning", "info"] }, { type: i1.FunctionExecutionResultComponent, selector: "dashboard-builder-post-processor-result", inputs: ["value", "valueProcessor"] }], directives: [{ type: i1.ElementPropertyLinkingDirective, selector: "[linkToElement][linkToProperty]", inputs: ["linkToElement", "linkToProperty", "linkToIndex", "currentValue"], outputs: ["currentValueChange"], exportAs: ["propertyLink"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], pipes: { "async": i3.AsyncPipe } });
FunctionBindingPropertiesComponent = FunctionBindingPropertiesComponent_1 = __decorate([
    AngularPropertiesElement,
    __metadata("design:paramtypes", [FunctionExecutionService])
], FunctionBindingPropertiesComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionBindingPropertiesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-function-binding-properties', template: "<div class=\"main\">\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"fun\">Function:</div>\n    <dashboard-builder-user-input\n      [error]=\"message?.type === 'error' ? message.text : ''\"\n      [info]=\"message?.type === 'info' ? message.text : ''\"\n    >\n      <davinci-text-area #codeArea multiline [linkToElement]=\"binding\" linkToProperty=\"fun\"></davinci-text-area>\n    </dashboard-builder-user-input>\n  </div>\n  <div class=\"grid-columns-4\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"updatePeriodMs\">Interval:</div>\n    <davinci-numeric-field\n      unit=\"ms\"\n      [linkToElement]=\"binding\"\n      linkToProperty=\"updatePeriodMs\"\n      min=\"0\"\n    ></davinci-numeric-field>\n  </div>\n\n  <dashboard-builder-post-processor-result *ngIf=\"message?.type !== 'error'\" [value]=\"bindingValue$ | async\">\n    <dashboard-function-binding\n      (bindingValueChanged)=\"bindingValueChanged($event)\"\n      *ngIf=\"binding.properties.fun\"\n      [fun]=\"binding.properties.fun\"\n      property=\"value\"\n    ></dashboard-function-binding>\n  </dashboard-builder-post-processor-result>\n</div>\n", styles: ["davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}.main{margin-top:-16px;padding-top:16px;padding-bottom:16px;border-top:1px solid var(--davinci-color-outline-light,#C5CACC)}.grid-columns-2,.grid-columns-4{position:relative;display:grid;grid-gap:10px 40px;gap:10px 40px;align-items:center;margin-bottom:1.6em;padding-right:36px}.grid-columns-2{grid-template-columns:120px 1fr}.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.FunctionExecutionService }]; }, propDecorators: { codeArea: [{
                type: ViewChild,
                args: ['codeArea', { static: true }]
            }] } });

class DashboardBuilderFunctionBindingModule {
}
DashboardBuilderFunctionBindingModule.REGISTER = [FunctionBindingComponent, FunctionBindingPropertiesComponent];
DashboardBuilderFunctionBindingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderFunctionBindingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderFunctionBindingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderFunctionBindingModule, declarations: [FunctionBindingComponent, FunctionBindingPropertiesComponent], imports: [CommonModule, DashboardBuilderCoreModule, MatTreeModule, MatIconModule, MatButtonModule] });
DashboardBuilderFunctionBindingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderFunctionBindingModule, imports: [[CommonModule, DashboardBuilderCoreModule, MatTreeModule, MatIconModule, MatButtonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderFunctionBindingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, DashboardBuilderCoreModule, MatTreeModule, MatIconModule, MatButtonModule],
                    declarations: [FunctionBindingComponent, FunctionBindingPropertiesComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

var ResultType;
(function (ResultType) {
    ResultType[ResultType["Ok"] = 0] = "Ok";
    ResultType[ResultType["Error"] = 1] = "Error";
})(ResultType || (ResultType = {}));
class RestConnectionService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.connectionInitiator = new Subject();
        this.connectionPool = this.connectionInitiator.pipe(
        // an observable (group) for each unique key will be created. "groupBy" will also discard the group once it's complete
        groupBy((config) => this.getKey(config)), map((group) => {
            // each group will become one connection that's only closed when there are no more subscribers
            const connection = group.pipe(first(), this.createRestConnection(), share());
            // unfortunately, performing any operation on a group (GroupedObservable) turns it into a normal observable, i.e. we can't get the key later even though we'll need it
            return {
                tag: group.key,
                obs$: connection,
            };
        }), 
        // need to preserve the state (grouping + active connections) between all subscribers
        multicast(new ReplaySubject()));
        this.connectionPool.connect();
    }
    connect(config) {
        const sanitizedConfig = this.sanitize(config);
        const key = this.getKey(sanitizedConfig);
        return defer(() => {
            // the order has to be (1) callee subscribes, (2) connection is initiated, hence the timeout
            setTimeout(() => this.connectionInitiator.next(sanitizedConfig));
            return this.connectionPool;
        }).pipe(
        // go fishing for the right connection
        find((tagged) => tagged.tag === key), 
        // this will cause the callee to actually subscribe to the data observable, not just the "managing" observable
        mergeMap((tagged) => tagged.obs$));
    }
    createRestConnection() {
        return (source$) => source$.pipe(switchMap((config) => {
            const request = this.httpClient.get(config.url, { responseType: 'text' }).pipe(map((data) => ({
                type: ResultType.Ok,
                data,
            })), catchError((error) => {
                return of({
                    type: ResultType.Error,
                    error,
                });
            }));
            const bt = new BestEffortSingleQueryTimer(request, config.interval);
            return bt.periodicData().pipe(withLatestFrom(bt.movingAverageRoundTripTime()), map(([result, avgRtt]) => (result.type === ResultType.Ok ? { ...result, avgRtt } : result)), retryWithDelay(config.interval));
        }));
    }
    getKey(config) {
        return JSON.stringify(config);
    }
    sanitize(config) {
        return {
            ...config,
            url: new URL(config.url).href,
        };
    }
}
RestConnectionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestConnectionService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
RestConnectionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestConnectionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestConnectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; } });

var RestBindingComponent_1;
let RestBindingComponent = RestBindingComponent_1 = class RestBindingComponent {
    constructor(restConnectionService, elRef, functionExecutionService) {
        this.restConnectionService = restConnectionService;
        this.updatePeriodMs = 1000;
        this.helper = new BindingHelper(elRef, functionExecutionService);
    }
    static interpretRestError(e) {
        if (e instanceof HttpErrorResponse) {
            if (e.status) {
                return `Server returned: ${e.status} ${e.statusText}`;
            }
            else {
                // status code 0 means the request was blocked by the browser. The application can never exactly know why, but we can make some educated guesses
                if (window.location.protocol === 'https' && e.url?.startsWith('http://')) {
                    return RestBindingComponent_1.ERROR_BLOCKED_INSECURE;
                }
                else {
                    return RestBindingComponent_1.ERROR_BLOCKED_GENERIC;
                }
            }
        }
        return RestBindingComponent_1.ERROR_SERVER_UNREACHABLE;
    }
    ngOnChanges() {
        this.subscription?.unsubscribe();
        if (this.url) {
            this.subscription = this.restConnectionService
                .connect({
                url: this.url,
                interval: this.updatePeriodMs,
            })
                .pipe(switchMap((result) => this.parseResult(result)), this.helper.parseJson(), this.helper.selectInJson(this.path), this.helper.applyPostProcessor(this.postProcessor))
                .subscribe((data) => this.helper.setValue(this.property, data));
        }
    }
    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }
    parseResult(result) {
        if (result.type === ResultType.Error) {
            this.helper.dispatchError(RestBindingComponent_1.ERROR_CONNECTION, RestBindingComponent_1.interpretRestError(result.error));
            return EMPTY;
        }
        this.helper.dispatchError(RestBindingComponent_1.ERROR_CONNECTION, undefined, true);
        const { data, avgRtt } = result;
        this.helper.dispatchWarning(BindingHelper.WARN_UPDATE_PERIOD, `Desired: ${this.updatePeriodMs}ms, actual: ${Math.floor(avgRtt)}ms`, avgRtt <= this.updatePeriodMs);
        return of(data);
    }
};
RestBindingComponent.ERROR_CONNECTION = 'Error while trying to connect.';
RestBindingComponent.ERROR_SERVER_UNREACHABLE = "Unable to connect. The server isn't available.\n" +
    '\n' +
    'Please check the entered URL in the settings for typos.\n' +
    '\n' +
    'If this problem remains, the server might be turned off. Please contact your administrator.';
RestBindingComponent.ERROR_BLOCKED_INSECURE = 'Failed to load data. The request was blocked by your browser.\n' +
    '\n' +
    'This application is loaded over HTTPS. The requested content must also be served over HTTPS to be secure.\n' +
    '\n' +
    'Although not recommended, you can disable this security feature by allowing insecure content for this page. ' +
    'Please note: The data transfer will then no longer be encrypted.';
RestBindingComponent.ERROR_BLOCKED_GENERIC = 'Failed to load data. The request was blocked by the browser. This most commonly occurs when the server takes too long to respond or the server is misconfigured.\n' +
    '\n' +
    "If this problem persists, check your network and/or server configuration. If the URL is otherwise reachable, you may have to adjust the server permissions by adding a 'Access-Control-Allow-Origin' header to responses or contact your administrator.";
RestBindingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestBindingComponent, deps: [{ token: RestConnectionService }, { token: i0.ElementRef }, { token: i1.FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
RestBindingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: RestBindingComponent, selector: "dashboard-builder-rest-binding", inputs: { property: "property", url: "url", updatePeriodMs: "updatePeriodMs", path: "path", postProcessor: "postProcessor" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true, encapsulation: i0.ViewEncapsulation.ShadowDom });
__decorate([
    Describe('URL to the REST endpoint'),
    __metadata("design:type", String)
], RestBindingComponent.prototype, "url", void 0);
__decorate([
    Describe('Update interval in milliseconds'),
    __metadata("design:type", Object)
], RestBindingComponent.prototype, "updatePeriodMs", void 0);
__decorate([
    Describe('Path to the target element from the root of the server response: e.g. devices.1.cpu. Only relevant for json response'),
    __metadata("design:type", String)
], RestBindingComponent.prototype, "path", void 0);
__decorate([
    Describe('Function to apply to the response: e.g. (point) => point.x'),
    __metadata("design:type", String)
], RestBindingComponent.prototype, "postProcessor", void 0);
RestBindingComponent = RestBindingComponent_1 = __decorate([
    AngularDashboardElement({
        tag: 'dashboard-rest-binding',
        role: 'behavior',
        documentation: {
            name: 'REST Binding',
            description: '',
        },
    }),
    __metadata("design:paramtypes", [RestConnectionService,
        ElementRef,
        FunctionExecutionService])
], RestBindingComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestBindingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dashboard-builder-rest-binding',
                    template: '',
                    encapsulation: ViewEncapsulation.ShadowDom,
                }]
        }], ctorParameters: function () { return [{ type: RestConnectionService }, { type: i0.ElementRef }, { type: i1.FunctionExecutionService }]; }, propDecorators: { property: [{
                type: Input
            }], url: [{
                type: Input
            }], updatePeriodMs: [{
                type: Input
            }], path: [{
                type: Input
            }], postProcessor: [{
                type: Input
            }] } });

class JsonNode {
    constructor(value, children, nodeName, type, path) {
        this.value = value;
        this.children = children;
        this.nodeName = nodeName;
        this.type = type;
        this.path = path;
    }
}
class RestBindingJsonSelectorComponent {
    constructor(http) {
        this.http = http;
        this.isLoading$ = new ReplaySubject();
        this.errorMessage$ = new Subject();
        this.infoMessage$ = new Subject();
        this.pathChange = new EventEmitter();
        this.hasNestedChild = (_, nodeData) => nodeData.type === 'object' && Boolean(nodeData.value);
        this.nestedTreeControl = new NestedTreeControl((node) => node.children);
        this.nestedDataSource = new MatTreeNestedDataSource();
    }
    ngOnChanges(changes) {
        if (changes.url) {
            this.fetchAndParseData().subscribe({
                complete: () => {
                    if (changes.path && this.path) {
                        this.expandPath(this.path);
                    }
                },
                error: (e) => this.errorMessage$.next(e),
            });
        }
        else if (changes.path && this.path) {
            this.expandPath(this.path);
        }
    }
    buildJsonTree(obj, parentPath = '') {
        return Object.keys(obj).map((key) => {
            const value = obj[key];
            const type = typeof value;
            const path = parentPath + key;
            const children = type === 'object' && value ? this.buildJsonTree(value, path + '.') : [];
            return new JsonNode(value, children, key, type, path);
        });
    }
    getType(node) {
        let description = (node.children
            .filter((childNode) => childNode.type !== 'object')
            .slice(0, 3)
            .map((childNode) => `${childNode.type} : ${this.formatValue(childNode)}`)
            .join(', ') || '') + ', ...';
        const maxLength = 50;
        if (description.length > maxLength) {
            description = description.substr(0, maxLength) + '...';
        }
        if (Array.isArray(node.value)) {
            return `[ ${description} ]`;
        }
        return `{ ${description} }`;
    }
    formatValue(node) {
        if (node.type === 'string') {
            return `"${node.value}"`;
        }
        return String(node.value);
    }
    selectNode(node) {
        this.path = node.path;
        this.pathChange.emit(node.path);
    }
    expandPath(path) {
        const nodes = this.nestedDataSource.data;
        const pathParts = path.split('.');
        if (pathParts.length < 2) {
            return;
        }
        let currentPath = pathParts[0];
        let iterationNode = nodes.find((n) => n.path === currentPath);
        for (let i = 1; i < pathParts.length - 1 && iterationNode; i++) {
            currentPath += '.' + pathParts[i];
            iterationNode = iterationNode.children?.find((child) => child.path === currentPath);
        }
        if (iterationNode) {
            this.nestedTreeControl.expand(iterationNode);
        }
    }
    fetchAndParseData() {
        return defer(() => {
            this.isLoading$.next(true);
            this.errorMessage$.next(undefined);
            this.infoMessage$.next(undefined);
            return this.http.get(this.url, { responseType: 'text' });
        }).pipe(catchError((e) => throwError(RestBindingComponent.interpretRestError(e))), map((jsonText) => JSON.parse(jsonText)), catchError(() => throwError(RestBindingJsonSelectorComponent.ERROR_JSON)), map((data) => {
            if (typeof data === 'object' && data) {
                return this.buildJsonTree(data);
            }
            else {
                this.infoMessage$.next(RestBindingJsonSelectorComponent.INFO_PRIMITIVE_DATA);
                return null;
            }
        }), finalize(() => this.isLoading$.next(false)), tap({
            next: (data) => (this.nestedDataSource.data = data),
            error: () => (this.nestedDataSource.data = null),
        }));
    }
}
RestBindingJsonSelectorComponent.INFO_PRIMITIVE_DATA = 'Data is a primitive data type. Nothing to select.';
RestBindingJsonSelectorComponent.ERROR_JSON = BindingHelper.ERROR_JSON;
RestBindingJsonSelectorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestBindingJsonSelectorComponent, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Component });
RestBindingJsonSelectorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: RestBindingJsonSelectorComponent, selector: "dashboard-builder-rest-binding-json-selector", inputs: { url: "url", path: "path" }, outputs: { pathChange: "pathChange" }, usesOnChanges: true, ngImport: i0, template: "<davinci-spinner *ngIf=\"isLoading$ | async\" class=\"loading-spinner\"></davinci-spinner>\n<davinci-callout type=\"error\" *ngIf=\"errorMessage$ | async as error\" [innerText]=\"error\"></davinci-callout>\n<davinci-callout type=\"info\" *ngIf=\"infoMessage$ | async as info\" [innerText]=\"info\"></davinci-callout>\n\n<mat-tree\n  *ngIf=\"nestedDataSource.data && (isLoading$ | async) === false\"\n  [dataSource]=\"nestedDataSource\"\n  [treeControl]=\"nestedTreeControl\"\n>\n  <mat-tree-node class=\"mat-tree-node\" *matTreeNodeDef=\"let node\" matTreeNodeToggle>\n    <code class=\"tree-node\" [class.selected]=\"node.path === path\" (click)=\"selectNode(node)\" [title]=\"node.path\">\n      {{ node.nodeName }}: <span class=\"node-value type-{{ node.type }}\">{{ formatValue(node) }}</span>\n    </code>\n  </mat-tree-node>\n\n  <mat-nested-tree-node class=\"mat-nexted-tree-node\" *matTreeNodeDef=\"let node; when: hasNestedChild\">\n    <div class=\"tree-node\" [class.selected]=\"node.path === path\">\n      <button class=\"button-tree-toggle\" matTreeNodeToggle [attr.aria-label]=\"'toggle ' + node.filename\">\n        <davinci-icon\n          [icon]=\"nestedTreeControl.isExpanded(node) ? 'navigation/expand_more' : 'navigation/chevron_right'\"\n        ></davinci-icon>\n      </button>\n      <code (click)=\"selectNode(node)\">\n        {{ node.nodeName }} : <span class=\"node-type\">{{ getType(node) }}</span>\n      </code>\n    </div>\n    <div class=\"tree-node-children\" [class.invisible]=\"!nestedTreeControl.isExpanded(node)\">\n      <ng-container matTreeNodeOutlet></ng-container>\n    </div>\n  </mat-nested-tree-node>\n</mat-tree>\n", styles: [":host{display:flex;flex-direction:column}.loading-spinner{margin:10px auto}.tree-node-children{margin-left:30px}.tree-node-children.invisible{display:none}.mat-tree{background:#f6f6f6;border:1px solid #e4e4e4;padding:1rem}.mat-tree-node{min-height:24px}.button-tree-toggle{background:none;border:none;padding:0;height:24px;align-items:center;white-space:pre;cursor:pointer}.tree-node{cursor:pointer;color:var(--davinci-color-deep-purple,#7000B0);display:block}.tree-node:active,.tree-node.selected{font-weight:900}.node-type{color:var(--davinci-color-secondary,#737F85)}.node-value{color:#000}.node-value.type-string{color:var(--davinci-color-namur-failed,#FF0000)}\n"], components: [{ type: i2.MatTree, selector: "mat-tree", exportAs: ["matTree"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.MatTreeNodeDef, selector: "[matTreeNodeDef]", inputs: ["matTreeNodeDefWhen", "matTreeNode"] }, { type: i2.MatTreeNode, selector: "mat-tree-node", inputs: ["role", "disabled", "tabIndex"], exportAs: ["matTreeNode"] }, { type: i2.MatTreeNodeToggle, selector: "[matTreeNodeToggle]", inputs: ["matTreeNodeToggleRecursive"] }, { type: i2.MatNestedTreeNode, selector: "mat-nested-tree-node", inputs: ["role", "disabled", "tabIndex", "matNestedTreeNode"], exportAs: ["matNestedTreeNode"] }, { type: i2.MatTreeNodeOutlet, selector: "[matTreeNodeOutlet]" }], pipes: { "async": i3.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestBindingJsonSelectorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-builder-rest-binding-json-selector', changeDetection: ChangeDetectionStrategy.OnPush, template: "<davinci-spinner *ngIf=\"isLoading$ | async\" class=\"loading-spinner\"></davinci-spinner>\n<davinci-callout type=\"error\" *ngIf=\"errorMessage$ | async as error\" [innerText]=\"error\"></davinci-callout>\n<davinci-callout type=\"info\" *ngIf=\"infoMessage$ | async as info\" [innerText]=\"info\"></davinci-callout>\n\n<mat-tree\n  *ngIf=\"nestedDataSource.data && (isLoading$ | async) === false\"\n  [dataSource]=\"nestedDataSource\"\n  [treeControl]=\"nestedTreeControl\"\n>\n  <mat-tree-node class=\"mat-tree-node\" *matTreeNodeDef=\"let node\" matTreeNodeToggle>\n    <code class=\"tree-node\" [class.selected]=\"node.path === path\" (click)=\"selectNode(node)\" [title]=\"node.path\">\n      {{ node.nodeName }}: <span class=\"node-value type-{{ node.type }}\">{{ formatValue(node) }}</span>\n    </code>\n  </mat-tree-node>\n\n  <mat-nested-tree-node class=\"mat-nexted-tree-node\" *matTreeNodeDef=\"let node; when: hasNestedChild\">\n    <div class=\"tree-node\" [class.selected]=\"node.path === path\">\n      <button class=\"button-tree-toggle\" matTreeNodeToggle [attr.aria-label]=\"'toggle ' + node.filename\">\n        <davinci-icon\n          [icon]=\"nestedTreeControl.isExpanded(node) ? 'navigation/expand_more' : 'navigation/chevron_right'\"\n        ></davinci-icon>\n      </button>\n      <code (click)=\"selectNode(node)\">\n        {{ node.nodeName }} : <span class=\"node-type\">{{ getType(node) }}</span>\n      </code>\n    </div>\n    <div class=\"tree-node-children\" [class.invisible]=\"!nestedTreeControl.isExpanded(node)\">\n      <ng-container matTreeNodeOutlet></ng-container>\n    </div>\n  </mat-nested-tree-node>\n</mat-tree>\n", styles: [":host{display:flex;flex-direction:column}.loading-spinner{margin:10px auto}.tree-node-children{margin-left:30px}.tree-node-children.invisible{display:none}.mat-tree{background:#f6f6f6;border:1px solid #e4e4e4;padding:1rem}.mat-tree-node{min-height:24px}.button-tree-toggle{background:none;border:none;padding:0;height:24px;align-items:center;white-space:pre;cursor:pointer}.tree-node{cursor:pointer;color:var(--davinci-color-deep-purple,#7000B0);display:block}.tree-node:active,.tree-node.selected{font-weight:900}.node-type{color:var(--davinci-color-secondary,#737F85)}.node-value{color:#000}.node-value.type-string{color:var(--davinci-color-namur-failed,#FF0000)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; }, propDecorators: { pathChange: [{
                type: Output
            }], url: [{
                type: Input
            }], path: [{
                type: Input
            }] } });

let RestBindingPropertiesComponent = class RestBindingPropertiesComponent extends SpecializedBindingPropertiesComponent {
    constructor(functionExecutionService) {
        super();
        this.functionExecutionService = functionExecutionService;
        this.expandJsonSelector = false;
        this.highlightTipPath = false;
        this.showExample = false;
        this.highlightTipUrl = false;
        this.postProcessorPlaceholder = 'e.g. (point) => point.x';
        this.isValidPostProcessor = false;
        this.postProcessorValue$ = new BehaviorSubject(undefined);
        this.postProcessor$ = this.postProcessorValue$.pipe(debounceTime(400));
        this.postProcessorError$ = this.postProcessor$.pipe(switchMap(async (postProcessor) => {
            if (!postProcessor) {
                return '';
            }
            const isValid = await this.functionExecutionService.isValid(postProcessor);
            return isValid ? '' : FunctionExecutionResultComponent.ERROR_FUNCTION_SYNTAX;
        }), tap$1((err) => {
            this.isValidPostProcessor = !err;
        }));
        this.bindingValue$ = new BehaviorSubject(undefined);
        this.urlValue$ = new BehaviorSubject('');
        this.url$ = this.urlValue$.pipe(debounceTime(400));
        this.initUrlHints();
    }
    ngOnInit() {
        if (this.binding.properties.path && this.binding.properties.url) {
            this.expandJsonSelector = true;
        }
    }
    toggleExpandJsonSelector() {
        this.expandJsonSelector = !this.expandJsonSelector;
    }
    setExampleUrl(urlField) {
        const exampleUrl = 'https://jsonplaceholder.typicode.com/todos';
        this.binding.properties.url = exampleUrl;
        urlField.value = exampleUrl;
        this.urlValue$.next(exampleUrl);
    }
    setPath(path) {
        this.binding.properties.path = path;
        this.propertyChange.emit({ property: 'path', value: path });
    }
    setExamplePath() {
        this.setPath('1.title');
    }
    toggleExampleExpandJsonSelector() {
        if (this.binding.properties.url) {
            this.toggleExpandJsonSelector();
        }
        else {
            this.highlightTipUrl = true;
            setTimeout(() => {
                this.highlightTipUrl = false;
            }, 200);
        }
    }
    setExamplePostProcessor(postProcessorLink) {
        if (this.binding.properties.path) {
            const examplePostProcessor = '(title) => title.length';
            postProcessorLink.currentValue = examplePostProcessor;
            postProcessorLink.triggerChanges(examplePostProcessor);
        }
        else {
            this.highlightTipPath = true;
            setTimeout(() => {
                this.highlightTipPath = false;
            }, 200);
        }
    }
    /**
     * 1: start: nothing set
     * 2: do expand the json input
     * 3: select a path in json
     * 4: collapse the json
     * 5: add a post processor function
     * 6: finished.
     */
    get tutorialStep() {
        if (!this.binding.properties.url) {
            return 1;
        }
        if (!this.binding.properties.path) {
            if (!this.expandJsonSelector) {
                return 2;
            }
            else {
                return 3;
            }
        }
        if (this.expandJsonSelector) {
            return 4;
        }
        if (!this.binding.properties.postProcessor) {
            return 5;
        }
        return 6;
    }
    onUrlChange(input) {
        // changing an existing binding causes this to be triggered with the current url, but in that case we don't want path to be reset
        if (input !== this.binding.properties.url) {
            this.setPath(undefined);
            this.urlValue$.next(input);
        }
    }
    postProcessorChanged(postProcessor) {
        this.postProcessorValue$.next(postProcessor);
    }
    bindingValueChanged(value) {
        this.bindingValue$.next(value.detail.value);
    }
    formHasErrors() {
        const { url } = this.binding.properties;
        return !this.getParsedUrl(url) || !this.isValidPostProcessor;
    }
    initUrlHints() {
        this.url$.subscribe({
            next: (input) => {
                this.urlErrorMessage = '';
                this.urlWarningMessage = '';
                if (!input) {
                    return;
                }
                const url = this.getParsedUrl(input);
                if (!url) {
                    this.urlErrorMessage = 'Invalid URL';
                }
                else if (!url.protocol.match(/https?:/)) {
                    this.urlErrorMessage = 'URL should start with http:// or https://';
                }
                else if (url.protocol === 'http:' && window.location.protocol === 'https:') {
                    this.urlWarningMessage =
                        'Using http:// URLs from a https site only works if you have insecure content enabled';
                }
            },
        });
    }
    getParsedUrl(input) {
        try {
            return new URL(input);
        }
        catch (e) {
            return undefined;
        }
    }
};
RestBindingPropertiesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestBindingPropertiesComponent, deps: [{ token: i1.FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
RestBindingPropertiesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: RestBindingPropertiesComponent, selector: "dashboard-rest-binding-properties", usesInheritance: true, ngImport: i0, template: "<div class=\"main\">\n  <div class=\"label help\">\n    <p>\n      Fetch data from a REST endpoint and parse its results.\n      <button class=\"example-button\" (click)=\"showExample = !showExample\">\n        {{ showExample ? 'Hide' : 'Show' }} example\n      </button>\n    </p>\n    <!--  Interactive explanation  -->\n    <ol *ngIf=\"showExample\">\n      <li [class.highlight]=\"highlightTipUrl || tutorialStep === 1\">\n        Paste your URL. <button class=\"example-button\" (click)=\"setExampleUrl(urlField)\">Example</button>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 2\">\n        Click on the\n        <davinci-icon\n          icon=\"action/search\"\n          class=\"icon-button\"\n          (click)=\"toggleExampleExpandJsonSelector()\"\n        ></davinci-icon>\n        button on \"URL\"\n      </li>\n      <li [class.highlight]=\"highlightTipPath || tutorialStep === 3\">\n        Select an entry in the shown JSON object you want to use as input data.\n        <button class=\"example-button\" (click)=\"setExamplePath()\">Example</button>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 4\">\n        Collapse the JSON expander\n        <davinci-icon icon=\"navigation/close\" class=\"icon-button\" (click)=\"expandJsonSelector = false\"></davinci-icon>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 5\">\n        Add a post processor function.\n        <button class=\"example-button\" (click)=\"setExamplePostProcessor(postProcessorLink)\">Example</button>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 6\">If you're finished hit \"Save\".</li>\n    </ol>\n  </div>\n\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"url\">URL:</div>\n    <dashboard-builder-user-input [error]=\"urlErrorMessage\" [warning]=\"urlWarningMessage\">\n      <davinci-text-field\n        #urlField\n        [linkToElement]=\"binding\"\n        linkToProperty=\"url\"\n        name=\"rest-binding-url\"\n        placeholder=\"http://... or https://...\"\n        (currentValueChange)=\"onUrlChange($event)\"\n      ></davinci-text-field>\n    </dashboard-builder-user-input>\n    <davinci-icon\n      class=\"icon-button\"\n      (click)=\"toggleExpandJsonSelector()\"\n      *ngIf=\"urlField.value\"\n      [icon]=\"expandJsonSelector ? 'navigation/close' : 'action/search'\"\n    ></davinci-icon>\n  </div>\n  <dashboard-builder-rest-binding-json-selector\n    *ngIf=\"expandJsonSelector\"\n    [url]=\"binding.properties.url\"\n    [path]=\"binding.properties.path\"\n    (pathChange)=\"setPath($event)\"\n  ></dashboard-builder-rest-binding-json-selector>\n  <div class=\"grid-columns-4\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"updatePeriodMs\">Interval:</div>\n    <davinci-numeric-field\n      unit=\"ms\"\n      [linkToElement]=\"binding\"\n      linkToProperty=\"updatePeriodMs\"\n      min=\"0\"\n    ></davinci-numeric-field>\n  </div>\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"postProcessor\">Post processor function:</div>\n    <dashboard-builder-user-input [error]=\"postProcessorError$ | async\">\n      <davinci-text-area\n        [placeholder]=\"postProcessorPlaceholder\"\n        [linkToElement]=\"binding\"\n        linkToProperty=\"postProcessor\"\n        #postProcessorLink=\"propertyLink\"\n        (currentValueChange)=\"postProcessorChanged($event)\"\n      ></davinci-text-area>\n    </dashboard-builder-user-input>\n  </div>\n\n  <dashboard-builder-post-processor-result\n    *ngIf=\"!urlErrorMessage\"\n    [valueProcessor]=\"postProcessor$ | async\"\n    [value]=\"bindingValue$ | async\"\n  >\n    <dashboard-rest-binding\n      (bindingValueChanged)=\"bindingValueChanged($event)\"\n      *ngIf=\"url$ | async\"\n      property=\"value\"\n      [url]=\"url$ | async\"\n      [path]=\"binding.properties.path\"\n    ></dashboard-rest-binding>\n  </dashboard-builder-post-processor-result>\n</div>\n", styles: ["davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}.main{margin-top:-16px;padding-top:16px;padding-bottom:16px;border-top:1px solid var(--davinci-color-outline-light,#C5CACC)}.grid-columns-2,.grid-columns-4{position:relative;display:grid;grid-gap:10px 40px;gap:10px 40px;align-items:center;margin-bottom:1.6em;padding-right:36px}.grid-columns-2{grid-template-columns:120px 1fr}.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}.icon-button{position:absolute;top:11px;right:0;width:36px;cursor:pointer}.help{margin-bottom:1.5rem}.help li{font-weight:400;transition:.4s ease}.help li.highlight{transition:.2s ease;font-weight:700}.help .example-button{cursor:pointer;text-decoration:underline;color:var(--davinci-color-dark-fuchsia,#7D256F);border:none;background:transparent}dashboard-builder-rest-binding-json-selector{margin-bottom:1.6em;margin-right:36px}\n"], components: [{ type: i1.HelpStateComponent, selector: "[helptext]", inputs: ["element", "property", "helptext", "hPos"] }, { type: i1.UserInputComponent, selector: "dashboard-builder-user-input", inputs: ["error", "warning", "info"] }, { type: RestBindingJsonSelectorComponent, selector: "dashboard-builder-rest-binding-json-selector", inputs: ["url", "path"], outputs: ["pathChange"] }, { type: i1.FunctionExecutionResultComponent, selector: "dashboard-builder-post-processor-result", inputs: ["value", "valueProcessor"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i1.ElementPropertyLinkingDirective, selector: "[linkToElement][linkToProperty]", inputs: ["linkToElement", "linkToProperty", "linkToIndex", "currentValue"], outputs: ["currentValueChange"], exportAs: ["propertyLink"] }], pipes: { "async": i3.AsyncPipe } });
RestBindingPropertiesComponent = __decorate([
    AngularPropertiesElement,
    __metadata("design:paramtypes", [FunctionExecutionService])
], RestBindingPropertiesComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: RestBindingPropertiesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-rest-binding-properties', template: "<div class=\"main\">\n  <div class=\"label help\">\n    <p>\n      Fetch data from a REST endpoint and parse its results.\n      <button class=\"example-button\" (click)=\"showExample = !showExample\">\n        {{ showExample ? 'Hide' : 'Show' }} example\n      </button>\n    </p>\n    <!--  Interactive explanation  -->\n    <ol *ngIf=\"showExample\">\n      <li [class.highlight]=\"highlightTipUrl || tutorialStep === 1\">\n        Paste your URL. <button class=\"example-button\" (click)=\"setExampleUrl(urlField)\">Example</button>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 2\">\n        Click on the\n        <davinci-icon\n          icon=\"action/search\"\n          class=\"icon-button\"\n          (click)=\"toggleExampleExpandJsonSelector()\"\n        ></davinci-icon>\n        button on \"URL\"\n      </li>\n      <li [class.highlight]=\"highlightTipPath || tutorialStep === 3\">\n        Select an entry in the shown JSON object you want to use as input data.\n        <button class=\"example-button\" (click)=\"setExamplePath()\">Example</button>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 4\">\n        Collapse the JSON expander\n        <davinci-icon icon=\"navigation/close\" class=\"icon-button\" (click)=\"expandJsonSelector = false\"></davinci-icon>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 5\">\n        Add a post processor function.\n        <button class=\"example-button\" (click)=\"setExamplePostProcessor(postProcessorLink)\">Example</button>\n      </li>\n      <li [class.highlight]=\"tutorialStep === 6\">If you're finished hit \"Save\".</li>\n    </ol>\n  </div>\n\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"url\">URL:</div>\n    <dashboard-builder-user-input [error]=\"urlErrorMessage\" [warning]=\"urlWarningMessage\">\n      <davinci-text-field\n        #urlField\n        [linkToElement]=\"binding\"\n        linkToProperty=\"url\"\n        name=\"rest-binding-url\"\n        placeholder=\"http://... or https://...\"\n        (currentValueChange)=\"onUrlChange($event)\"\n      ></davinci-text-field>\n    </dashboard-builder-user-input>\n    <davinci-icon\n      class=\"icon-button\"\n      (click)=\"toggleExpandJsonSelector()\"\n      *ngIf=\"urlField.value\"\n      [icon]=\"expandJsonSelector ? 'navigation/close' : 'action/search'\"\n    ></davinci-icon>\n  </div>\n  <dashboard-builder-rest-binding-json-selector\n    *ngIf=\"expandJsonSelector\"\n    [url]=\"binding.properties.url\"\n    [path]=\"binding.properties.path\"\n    (pathChange)=\"setPath($event)\"\n  ></dashboard-builder-rest-binding-json-selector>\n  <div class=\"grid-columns-4\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"updatePeriodMs\">Interval:</div>\n    <davinci-numeric-field\n      unit=\"ms\"\n      [linkToElement]=\"binding\"\n      linkToProperty=\"updatePeriodMs\"\n      min=\"0\"\n    ></davinci-numeric-field>\n  </div>\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"postProcessor\">Post processor function:</div>\n    <dashboard-builder-user-input [error]=\"postProcessorError$ | async\">\n      <davinci-text-area\n        [placeholder]=\"postProcessorPlaceholder\"\n        [linkToElement]=\"binding\"\n        linkToProperty=\"postProcessor\"\n        #postProcessorLink=\"propertyLink\"\n        (currentValueChange)=\"postProcessorChanged($event)\"\n      ></davinci-text-area>\n    </dashboard-builder-user-input>\n  </div>\n\n  <dashboard-builder-post-processor-result\n    *ngIf=\"!urlErrorMessage\"\n    [valueProcessor]=\"postProcessor$ | async\"\n    [value]=\"bindingValue$ | async\"\n  >\n    <dashboard-rest-binding\n      (bindingValueChanged)=\"bindingValueChanged($event)\"\n      *ngIf=\"url$ | async\"\n      property=\"value\"\n      [url]=\"url$ | async\"\n      [path]=\"binding.properties.path\"\n    ></dashboard-rest-binding>\n  </dashboard-builder-post-processor-result>\n</div>\n", styles: ["davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}.main{margin-top:-16px;padding-top:16px;padding-bottom:16px;border-top:1px solid var(--davinci-color-outline-light,#C5CACC)}.grid-columns-2,.grid-columns-4{position:relative;display:grid;grid-gap:10px 40px;gap:10px 40px;align-items:center;margin-bottom:1.6em;padding-right:36px}.grid-columns-2{grid-template-columns:120px 1fr}.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}.icon-button{position:absolute;top:11px;right:0;width:36px;cursor:pointer}.help{margin-bottom:1.5rem}.help li{font-weight:400;transition:.4s ease}.help li.highlight{transition:.2s ease;font-weight:700}.help .example-button{cursor:pointer;text-decoration:underline;color:var(--davinci-color-dark-fuchsia,#7D256F);border:none;background:transparent}dashboard-builder-rest-binding-json-selector{margin-bottom:1.6em;margin-right:36px}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.FunctionExecutionService }]; } });

class DashboardBuilderRestBindingModule {
}
DashboardBuilderRestBindingModule.REGISTER = [RestBindingComponent, RestBindingPropertiesComponent];
DashboardBuilderRestBindingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderRestBindingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderRestBindingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderRestBindingModule, declarations: [RestBindingComponent, RestBindingPropertiesComponent, RestBindingJsonSelectorComponent], imports: [CommonModule, DashboardBuilderCoreModule, MatTreeModule, MatIconModule, MatButtonModule] });
DashboardBuilderRestBindingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderRestBindingModule, imports: [[CommonModule, DashboardBuilderCoreModule, MatTreeModule, MatIconModule, MatButtonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderRestBindingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, DashboardBuilderCoreModule, MatTreeModule, MatIconModule, MatButtonModule],
                    declarations: [RestBindingComponent, RestBindingPropertiesComponent, RestBindingJsonSelectorComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class WebSocketConnectionService {
    constructor() {
        this.connections = new Map();
    }
    connect(config) {
        const { url: userUrl, openObserver, closeObserver, reconnectDelay } = config;
        // href is normalized, i.e. root domain will always contain a trailing slash, but path will only end in / if the input did
        const url = new URL(userUrl).href;
        return defer(() => {
            const connection = this.connections.get(url) ?? this.createConnection(url);
            const ref = { reconnectDelay };
            connection.refs.push(ref);
            const openSubscription = openObserver && connection.open$.subscribe(openObserver);
            const closeSubscription = closeObserver && connection.close$.subscribe(closeObserver);
            if (connection.lastOpenCloseEvent) {
                const obs = connection.lastOpenCloseEvent.type === 'open' ? openObserver : closeObserver;
                obs?.next(connection.lastOpenCloseEvent);
            }
            return connection.ws$.pipe(finalize(() => {
                connection.refs = connection.refs.filter((r) => r !== ref);
                openSubscription?.unsubscribe();
                closeSubscription?.unsubscribe();
                if (connection.refs.length === 0) {
                    clearTimeout(connection.reconnectTimeout);
                    connection.stop$.next();
                    this.connections.delete(url);
                }
            }));
        });
    }
    createConnection(url) {
        const connections$ = new Subject();
        const ws$ = new ReplaySubject(1);
        const open$ = new Subject();
        const close$ = new Subject();
        const stop$ = new Subject();
        connections$.subscribe({
            next: () => {
                stop$.next();
                webSocket({
                    url,
                    openObserver: open$,
                    closeObserver: close$,
                    deserializer: ({ data }) => data,
                })
                    .pipe(takeUntil(stop$))
                    .subscribe({
                    next: (data) => ws$.next(data),
                    error: (err) => {
                        if (err?.target?.readyState !== WebSocket.CLOSED) {
                            ws$.error(err);
                        }
                    },
                });
            },
        });
        connections$.next();
        open$.subscribe({
            next: (e) => {
                const connection = this.connections.get(url);
                if (connection) {
                    clearTimeout(connection.reconnectTimeout);
                    connection.lastOpenCloseEvent = e;
                }
            },
        });
        close$.subscribe({
            next: (e) => {
                const connection = this.connections.get(url);
                if (connection) {
                    clearTimeout(connection.reconnectTimeout);
                    connection.reconnectTimeout = setTimeout(() => connections$.next(), Math.min(...connection.refs.map((r) => r.reconnectDelay)));
                    connection.lastOpenCloseEvent = e;
                }
            },
        });
        const connection = {
            ws$,
            open$,
            close$,
            stop$,
            refs: [],
            reconnectTimeout: null,
            lastOpenCloseEvent: null,
        };
        this.connections.set(url, connection);
        return connection;
    }
}
WebSocketConnectionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketConnectionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
WebSocketConnectionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketConnectionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketConnectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

var WebSocketBindingComponent_1;
let WebSocketBindingComponent = WebSocketBindingComponent_1 = class WebSocketBindingComponent {
    constructor(webSocketConnectionService, elRef, functionExecutionService) {
        this.webSocketConnectionService = webSocketConnectionService;
        this.reconnectDelayMs = 5000;
        this.helper = new BindingHelper(elRef, functionExecutionService);
    }
    ngOnChanges(changes) {
        if (changes.url || changes.path || changes.postProcessor || changes.reconnectDelayMs) {
            clearTimeout(this.subscribeTimeout);
            // timeout needed to let initial value kick-in - prevents issues with inconsistent ui when ws keeps sending constant value
            this.subscribeTimeout = setTimeout(() => {
                this.connect();
            }, 10);
        }
    }
    ngOnDestroy() {
        clearTimeout(this.subscribeTimeout);
        this.subscription?.unsubscribe();
    }
    connect() {
        this.subscription?.unsubscribe();
        this.subscription = this.webSocketConnectionService
            .connect({
            url: this.url,
            reconnectDelay: this.reconnectDelayMs,
            openObserver: {
                next: () => this.helper.dispatchError(WebSocketBindingComponent_1.CONNECTION_CLOSED, undefined, true),
            },
            closeObserver: {
                next: () => this.helper.dispatchError(WebSocketBindingComponent_1.CONNECTION_CLOSED),
            },
        })
            .pipe(this.helper.parseJson(), this.helper.selectInJson(this.path), this.helper.applyPostProcessor(this.postProcessor))
            .subscribe({
            next: (value) => this.helper.setValue(this.property, value),
            error: (err) => this.helper.dispatchError(WebSocketBindingComponent_1.CONNECTION_CLOSED, `Reason: ${err instanceof Error ? err.message : err}`),
        });
    }
};
WebSocketBindingComponent.CONNECTION_CLOSED = 'Unable to connect to server.';
WebSocketBindingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketBindingComponent, deps: [{ token: WebSocketConnectionService }, { token: i0.ElementRef }, { token: i1.FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
WebSocketBindingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: WebSocketBindingComponent, selector: "dashboard-websocket-binding", inputs: { property: "property", url: "url", path: "path", postProcessor: "postProcessor", reconnectDelayMs: "reconnectDelayMs" }, usesOnChanges: true, ngImport: i0, template: '', isInline: true, encapsulation: i0.ViewEncapsulation.ShadowDom });
__decorate([
    Describe('URL to the WebSocket service'),
    __metadata("design:type", String)
], WebSocketBindingComponent.prototype, "url", void 0);
__decorate([
    Describe('Path to the target element from the root of the received message: e.g. devices.1.cpu. Only relevant if message payload is json'),
    __metadata("design:type", String)
], WebSocketBindingComponent.prototype, "path", void 0);
__decorate([
    Describe('Function to apply to the target element: e.g. (point) => Math.floor(point)'),
    __metadata("design:type", String)
], WebSocketBindingComponent.prototype, "postProcessor", void 0);
__decorate([
    Describe('Delay between reconnect attempts when connection is lost'),
    __metadata("design:type", Object)
], WebSocketBindingComponent.prototype, "reconnectDelayMs", void 0);
WebSocketBindingComponent = WebSocketBindingComponent_1 = __decorate([
    AngularDashboardElement({
        role: 'behavior',
        documentation: {
            name: 'WebSocket Binding',
            description: '',
        },
    }),
    __metadata("design:paramtypes", [WebSocketConnectionService,
        ElementRef,
        FunctionExecutionService])
], WebSocketBindingComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketBindingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dashboard-websocket-binding',
                    template: '',
                    encapsulation: ViewEncapsulation.ShadowDom,
                }]
        }], ctorParameters: function () { return [{ type: WebSocketConnectionService }, { type: i0.ElementRef }, { type: i1.FunctionExecutionService }]; }, propDecorators: { property: [{
                type: Input
            }], url: [{
                type: Input
            }], path: [{
                type: Input
            }], postProcessor: [{
                type: Input
            }], reconnectDelayMs: [{
                type: Input
            }] } });

var WebSocketBindingPropertiesComponent_1;
let WebSocketBindingPropertiesComponent = WebSocketBindingPropertiesComponent_1 = class WebSocketBindingPropertiesComponent extends SpecializedBindingPropertiesComponent {
    constructor(functionExecutionService) {
        super();
        this.functionExecutionService = functionExecutionService;
        this.isValidPostProcessor = false;
        this.postProcessorValue$ = new BehaviorSubject(undefined);
        this.postProcessor$ = this.postProcessorValue$.pipe(debounceTime(400));
        this.postProcessorError$ = this.postProcessor$.pipe(switchMap(async (postProcessor) => {
            if (!postProcessor) {
                return '';
            }
            const isValid = await this.functionExecutionService.isValid(postProcessor);
            return isValid ? '' : FunctionExecutionResultComponent.ERROR_FUNCTION_SYNTAX;
        }), tap$1((err) => {
            this.isValidPostProcessor = !err;
        }));
        this.urlValue$ = new BehaviorSubject('');
        this.url$ = this.urlValue$.pipe(debounceTime(400));
        this.initUrlHints();
    }
    static getDefaultPort(urlProtocol) {
        switch (urlProtocol) {
            case 'wss:':
            case 'https:':
                return 443;
            case 'ws:':
            case 'http:':
                return 80;
            default:
                return undefined;
        }
    }
    onUrlChange(input) {
        this.urlValue$.next(input);
    }
    postProcessorChanged(postProcessor) {
        this.postProcessorValue$.next(postProcessor);
    }
    formHasErrors() {
        const { url } = this.binding.properties;
        return !this.getParsedUrl(url) || !this.isValidPostProcessor;
    }
    initUrlHints() {
        this.url$.subscribe({
            next: (input) => {
                this.urlErrorMessage = '';
                this.urlWarningMessage = '';
                if (!input) {
                    return;
                }
                const url = this.getParsedUrl(input);
                if (!url) {
                    this.urlErrorMessage = 'Invalid URL';
                }
                else if (!url.protocol.match(/wss?:/)) {
                    this.urlErrorMessage = 'URL should start with ws:// or wss://';
                }
                else if ((url.protocol === 'ws:' || url.protocol === 'http:') && window.location.protocol === 'https:') {
                    this.urlWarningMessage = 'Using ws:// URLs from a https site only works if you have insecure content enabled';
                }
                else if (!input.match(/:\d+/)) {
                    const defaultPort = WebSocketBindingPropertiesComponent_1.getDefaultPort(url.protocol);
                    const defaultPortStr = defaultPort ? ` (${defaultPort})` : '';
                    this.urlWarningMessage = `Using default port${defaultPortStr}`;
                }
            },
        });
    }
    getParsedUrl(input) {
        try {
            return new URL(input);
        }
        catch (e) {
            return undefined;
        }
    }
};
WebSocketBindingPropertiesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketBindingPropertiesComponent, deps: [{ token: i1.FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
WebSocketBindingPropertiesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: WebSocketBindingPropertiesComponent, selector: "dashboard-websocket-binding-properties", usesInheritance: true, ngImport: i0, template: "<div class=\"main\">\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"url\">WebSocket URL:</div>\n    <dashboard-builder-user-input [error]=\"urlErrorMessage\" [warning]=\"urlWarningMessage\">\n      <davinci-text-field\n        placeholder=\"ws://... or wss://...\"\n        [linkToElement]=\"binding\"\n        linkToProperty=\"url\"\n        name=\"web-socket-binding-url\"\n        (currentValueChange)=\"onUrlChange($event)\"\n      ></davinci-text-field>\n    </dashboard-builder-user-input>\n  </div>\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"path\">Path:</div>\n    <davinci-text-field [linkToElement]=\"binding\" linkToProperty=\"path\"></davinci-text-field>\n  </div>\n  <div class=\"grid-columns-4\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"reconnectDelayMs\">Reconnect delay:</div>\n    <davinci-numeric-field\n      min=\"0\"\n      unit=\"ms\"\n      [linkToElement]=\"binding\"\n      linkToProperty=\"reconnectDelayMs\"\n    ></davinci-numeric-field>\n  </div>\n  <div class=\"grid-columns-2\">\n    <div helptext [element]=\"binding\" property=\"postProcessor\">Post processor function:</div>\n    <dashboard-builder-user-input [error]=\"postProcessorError$ | async\">\n      <davinci-text-area\n        placeholder=\"e.g. (point) => point.x\"\n        [linkToElement]=\"binding\"\n        linkToProperty=\"postProcessor\"\n        (currentValueChange)=\"postProcessorChanged($event)\"\n        (input)=\"postProcessorChanged($event.detail)\"\n      ></davinci-text-area>\n    </dashboard-builder-user-input>\n  </div>\n</div>\n", styles: ["davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}.main{margin-top:-16px;padding-top:16px;padding-bottom:16px;border-top:1px solid var(--davinci-color-outline-light,#C5CACC)}.grid-columns-2,.grid-columns-4{position:relative;display:grid;grid-gap:10px 40px;gap:10px 40px;align-items:center;margin-bottom:1.6em;padding-right:36px}.grid-columns-2{grid-template-columns:120px 1fr}.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}\n"], components: [{ type: i1.HelpStateComponent, selector: "[helptext]", inputs: ["element", "property", "helptext", "hPos"] }, { type: i1.UserInputComponent, selector: "dashboard-builder-user-input", inputs: ["error", "warning", "info"] }], directives: [{ type: i1.ElementPropertyLinkingDirective, selector: "[linkToElement][linkToProperty]", inputs: ["linkToElement", "linkToProperty", "linkToIndex", "currentValue"], outputs: ["currentValueChange"], exportAs: ["propertyLink"] }], pipes: { "async": i3.AsyncPipe } });
WebSocketBindingPropertiesComponent = WebSocketBindingPropertiesComponent_1 = __decorate([
    AngularPropertiesElement,
    __metadata("design:paramtypes", [FunctionExecutionService])
], WebSocketBindingPropertiesComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: WebSocketBindingPropertiesComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-websocket-binding-properties', template: "<div class=\"main\">\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"url\">WebSocket URL:</div>\n    <dashboard-builder-user-input [error]=\"urlErrorMessage\" [warning]=\"urlWarningMessage\">\n      <davinci-text-field\n        placeholder=\"ws://... or wss://...\"\n        [linkToElement]=\"binding\"\n        linkToProperty=\"url\"\n        name=\"web-socket-binding-url\"\n        (currentValueChange)=\"onUrlChange($event)\"\n      ></davinci-text-field>\n    </dashboard-builder-user-input>\n  </div>\n  <div class=\"grid-columns-2\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"path\">Path:</div>\n    <davinci-text-field [linkToElement]=\"binding\" linkToProperty=\"path\"></davinci-text-field>\n  </div>\n  <div class=\"grid-columns-4\">\n    <div class=\"label\" helptext [element]=\"binding\" property=\"reconnectDelayMs\">Reconnect delay:</div>\n    <davinci-numeric-field\n      min=\"0\"\n      unit=\"ms\"\n      [linkToElement]=\"binding\"\n      linkToProperty=\"reconnectDelayMs\"\n    ></davinci-numeric-field>\n  </div>\n  <div class=\"grid-columns-2\">\n    <div helptext [element]=\"binding\" property=\"postProcessor\">Post processor function:</div>\n    <dashboard-builder-user-input [error]=\"postProcessorError$ | async\">\n      <davinci-text-area\n        placeholder=\"e.g. (point) => point.x\"\n        [linkToElement]=\"binding\"\n        linkToProperty=\"postProcessor\"\n        (currentValueChange)=\"postProcessorChanged($event)\"\n        (input)=\"postProcessorChanged($event.detail)\"\n      ></davinci-text-area>\n    </dashboard-builder-user-input>\n  </div>\n</div>\n", styles: ["davinci-text-field,davinci-drop-down,davinci-numeric-field,davinci-text-area{margin:0;width:100%}davinci-text-area{height:8.57143rem}.main{margin-top:-16px;padding-top:16px;padding-bottom:16px;border-top:1px solid var(--davinci-color-outline-light,#C5CACC)}.grid-columns-2,.grid-columns-4{position:relative;display:grid;grid-gap:10px 40px;gap:10px 40px;align-items:center;margin-bottom:1.6em;padding-right:36px}.grid-columns-2{grid-template-columns:120px 1fr}.grid-columns-4{grid-template-columns:120px 1fr 80px 1fr}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.FunctionExecutionService }]; } });

class DashboardBuilderWebSocketBindingModule {
}
DashboardBuilderWebSocketBindingModule.REGISTER = [WebSocketBindingComponent, WebSocketBindingPropertiesComponent];
DashboardBuilderWebSocketBindingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderWebSocketBindingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderWebSocketBindingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderWebSocketBindingModule, declarations: [WebSocketBindingComponent, WebSocketBindingPropertiesComponent], imports: [CommonModule, DashboardBuilderCoreModule] });
DashboardBuilderWebSocketBindingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderWebSocketBindingModule, imports: [[CommonModule, DashboardBuilderCoreModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderWebSocketBindingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, DashboardBuilderCoreModule],
                    declarations: [WebSocketBindingComponent, WebSocketBindingPropertiesComponent],
                }]
        }] });

class DashboardBuilderDefaultBindingsModule {
}
DashboardBuilderDefaultBindingsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderDefaultBindingsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderDefaultBindingsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderDefaultBindingsModule, imports: [DashboardBuilderRestBindingModule,
        DashboardBuilderFunctionBindingModule,
        DashboardBuilderWebSocketBindingModule] });
DashboardBuilderDefaultBindingsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderDefaultBindingsModule, imports: [[
            DashboardBuilderRestBindingModule,
            DashboardBuilderFunctionBindingModule,
            DashboardBuilderWebSocketBindingModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderDefaultBindingsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DashboardBuilderRestBindingModule,
                        DashboardBuilderFunctionBindingModule,
                        DashboardBuilderWebSocketBindingModule,
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DashboardBuilderDefaultBindingsModule };
